import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CarFront,
  CheckCircle2,
  KeyRound,
  Loader2,
  Plus,
  ShieldAlert,
  Tag,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

export interface Veiculo {
  seq_veiculo: number;
  seq_cliente: number;
  num_placa: string;
  nom_marca: string | null;
  nom_modelo: string | null;
  des_cor: string | null;
  cod_status: string;
  cod_apelido: string | null;
}

/**
 * Dispara WhatsApp anti-fraude (best-effort, ignora falha).
 * Renderiza template via edge function enviar-mensagem-uazapi.
 */
async function dispararWhatsappAntiFraude(
  codTemplate: 'VEICULO_VENDA_DECLARADA' | 'VEICULO_PLACA_ALTERADA',
  seqCliente: number,
  variaveis: Record<string, string | number>,
): Promise<void> {
  try {
    const { data: cli } = await supabase
      .from('tab_clientes')
      .select('num_whatsapp, nom_cliente')
      .eq('seq_cliente', seqCliente)
      .maybeSingle();
    if (!cli?.num_whatsapp) return;
    await supabase.functions.invoke('enviar-mensagem-uazapi', {
      body: {
        cod_template: codTemplate,
        num_destino: cli.num_whatsapp,
        seq_cliente: seqCliente,
        variaveis: { nom_cliente: cli.nom_cliente, ...variaveis },
      },
    });
  } catch {
    // best-effort — não bloqueia UX
  }
}

const MOTIVOS_TROCA = [
  { value: 'ERRO_CADASTRO', label: 'Erro no cadastro original' },
  { value: 'TRANSFERENCIA_PROPRIEDADE', label: 'Transferi a propriedade do veículo' },
  { value: 'EMPLACAMENTO_NOVO', label: 'Mudanca de placa antiga -> Mercosul' },
  { value: 'OUTRO', label: 'Outro motivo' },
];

export function VeiculosPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [trocaAberto, setTrocaAberto] = useState<Veiculo | null>(null);
  const [vendaAberto, setVendaAberto] = useState<Veiculo | null>(null);

  const veiculos = useQuery({
    queryKey: ['cliente-veiculos', user?.id],
    queryFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      if (!cli) return [];
      const { data, error } = await supabase
        .from('tab_veiculos')
        .select(
          'seq_veiculo, seq_cliente, num_placa, nom_marca, nom_modelo, des_cor, cod_status, cod_apelido',
        )
        .eq('seq_cliente', cli.seq_cliente)
        .order('dta_criacao', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Veiculo[];
    },
    enabled: !!user,
  });

  useRealtimeInvalidate('tab_veiculos', ['cliente-veiculos', user?.id]);

  const trocarPlaca = useMutation({
    mutationFn: async (vars: {
      seq: number;
      placaAntiga: string;
      nova: string;
      motivo: string;
      des: string;
      seqCliente?: number;
    }) => {
      const { error } = await sb.rpc('fn_trocar_placa_veiculo', {
        p_seq_veiculo: vars.seq,
        p_nova_placa: vars.nova,
        p_cod_motivo: vars.motivo,
        p_des_motivo: vars.des || null,
      });
      if (error) throw error;
      // WhatsApp anti-fraude (best-effort, ignora falha)
      if (vars.seqCliente) {
        await dispararWhatsappAntiFraude('VEICULO_PLACA_ALTERADA', vars.seqCliente, {
          num_placa_anterior: vars.placaAntiga,
          num_placa_nova: vars.nova,
        });
      }
    },
    onSuccess: () => {
      toast.success('Placa atualizada com sucesso', {
        description: 'Enviamos um WhatsApp de confirmação.',
      });
      void queryClient.invalidateQueries({ queryKey: ['cliente-veiculos'] });
      setTrocaAberto(null);
    },
    onError: (e) => toast.error('Falha ao trocar placa', { description: traduzirErroSupabase(e) }),
  });

  const venderVeiculo = useMutation({
    mutationFn: async (vars: {
      seq: number;
      motivo: string;
      placa: string;
      seqCliente?: number;
    }) => {
      const { error } = await sb.rpc('fn_marcar_veiculo_vendido', {
        p_seq_veiculo: vars.seq,
        p_des_motivo: vars.motivo || null,
      });
      if (error) throw error;
      if (vars.seqCliente) {
        await dispararWhatsappAntiFraude('VEICULO_VENDA_DECLARADA', vars.seqCliente, {
          num_placa: vars.placa,
          cod_motivo: 'VENDA',
          dias_bloqueio: 60, // alinhado com VEICULO_BLOQUEIA_REATIVAR_PLACA_DIAS default
          dta_liberacao: new Date(Date.now() + 60 * 86_400_000).toLocaleDateString('pt-BR'),
        });
      }
    },
    onSuccess: () => {
      toast.success('Veículo removido', {
        description:
          'Enviamos um WhatsApp de confirmação. A placa fica bloqueada por alguns dias antes de poder ser cadastrada de novo.',
      });
      void queryClient.invalidateQueries({ queryKey: ['cliente-veiculos'] });
      setVendaAberto(null);
    },
    onError: (e) =>
      toast.error('Falha ao remover veículo', { description: traduzirErroSupabase(e) }),
  });

  const ativos = veiculos.data?.filter((v) => v.cod_status === 'ATIVO').length ?? 0;

  return (
    <div className="space-y-5 p-4 pb-24">
      <div className="octa-page-header octa-fade-in-up flex items-end justify-between pb-3">
        <div className="space-y-1.5">
          <p className="text-overline text-muted-foreground uppercase">Sua frota</p>
          <h1>Meus veículos</h1>
          {ativos > 0 && (
            <p>
              {ativos} {ativos === 1 ? 'veículo ativo' : 'veículos ativos'}
            </p>
          )}
        </div>
        <Link to="/veiculos/adicionar">
          <Button size="sm" className="rounded-xl">
            <Plus className="mr-1 h-4 w-4" /> Adicionar
          </Button>
        </Link>
      </div>

      {veiculos.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : veiculos.data?.length === 0 ? (
        <EmptyState
          variant="hero"
          icon={CarFront}
          title="Nenhum veículo ainda"
          description="Adicione seu primeiro veículo para começar a economizar nas lavagens."
          action={
            <Link to="/veiculos/adicionar">
              <Button size="lg" className="rounded-2xl">
                <Plus className="mr-1 h-4 w-4" /> Adicionar veículo
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="octa-stagger space-y-3">
          {veiculos.data?.map((v) => {
            const ativo = v.cod_status === 'ATIVO';
            return (
              <Link
                key={v.seq_veiculo}
                to={`/veiculos/${v.seq_veiculo}`}
                className="octa-card-tap block"
                aria-label={`Detalhes do veiculo ${v.num_placa}`}
              >
                <Card variant="elevated">
                  <CardContent className="flex items-center gap-3 p-4">
                    <div
                      className={`shadow-octa-sm flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${
                        ativo
                          ? 'bg-gradient-to-br from-sky-500 to-blue-700 text-white'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <CarFront className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-base font-extrabold tracking-wider">{v.num_placa}</p>
                        <StatusBadge variant={ativo ? 'success' : 'neutral'} dot>
                          {v.cod_status.replace('_', ' ')}
                        </StatusBadge>
                      </div>
                      <p className="text-muted-foreground mt-0.5 truncate text-xs">
                        {v.cod_apelido ?? `${v.nom_marca ?? ''} ${v.nom_modelo ?? ''}`.trim()}
                        {v.des_cor && ` · ${v.des_cor}`}
                      </p>
                    </div>

                    {ativo && (
                      <div className="flex gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setTrocaAberto(v);
                          }}
                          aria-label="Mudar placa"
                          title="Mudar placa"
                          className="h-9 w-9"
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setVendaAberto(v);
                          }}
                          aria-label="Vendi este carro"
                          title="Vendi este carro"
                          className="h-9 w-9 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950/30"
                        >
                          <Tag className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Modal: trocar placa */}
      {trocaAberto && (
        <TrocarPlacaDialog
          veiculo={trocaAberto}
          onSubmit={(nova, motivo, des) =>
            trocarPlaca.mutate({
              seq: trocaAberto.seq_veiculo,
              placaAntiga: trocaAberto.num_placa,
              nova,
              motivo,
              des,
              seqCliente: trocaAberto.seq_cliente,
            })
          }
          onClose={() => setTrocaAberto(null)}
          carregando={trocarPlaca.isPending}
        />
      )}

      {/* Modal: vendi este carro */}
      {vendaAberto && (
        <VenderVeiculoDialog
          veiculo={vendaAberto}
          onSubmit={(motivo) =>
            venderVeiculo.mutate({
              seq: vendaAberto.seq_veiculo,
              motivo,
              placa: vendaAberto.num_placa,
              seqCliente: vendaAberto.seq_cliente,
            })
          }
          onClose={() => setVendaAberto(null)}
          carregando={venderVeiculo.isPending}
        />
      )}
    </div>
  );
}

// =============================================================================
// Modal: Mudar placa (com motivo obrigatório)
// Exportado pra ser reusado na tela de detalhe do veiculo.
// =============================================================================
export function TrocarPlacaDialog({
  veiculo,
  onSubmit,
  onClose,
  carregando,
}: {
  veiculo: Veiculo;
  onSubmit: (nova: string, motivo: string, des: string) => void;
  onClose: () => void;
  carregando: boolean;
}) {
  const [novaPlaca, setNovaPlaca] = useState('');
  const [motivo, setMotivo] = useState('');
  const [descricao, setDescricao] = useState('');

  const placaNorm = novaPlaca.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const placaValida =
    /^[A-Z]{3}[0-9][A-Z][0-9]{2}$/.test(placaNorm) || /^[A-Z]{3}[0-9]{4}$/.test(placaNorm);
  const podeEnviar = placaValida && motivo && placaNorm !== veiculo.num_placa;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border/40 rounded-3xl sm:max-w-md">
        <DialogHeader className="space-y-2.5">
          <div className="bg-octa-gradient shadow-octa-glow mx-auto flex h-12 w-12 items-center justify-center rounded-2xl text-white">
            <KeyRound className="h-5 w-5" />
          </div>
          <DialogTitle className="text-display-sm text-center font-extrabold tracking-tight">
            Mudar placa
          </DialogTitle>
          <DialogDescription className="text-center">
            Placa atual:{' '}
            <span className="text-foreground font-bold tracking-wider">{veiculo.num_placa}</span>
            <br />
            <span className="text-muted-foreground text-xs">
              Por seguranca, a troca passa por carencia e exige motivo.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="nova-placa" className="text-xs font-semibold uppercase tracking-wider">
              Nova placa
            </Label>
            <Input
              id="nova-placa"
              value={novaPlaca}
              onChange={(e) => setNovaPlaca(e.target.value.toUpperCase())}
              placeholder="ABC1D23"
              className="h-12 rounded-xl text-lg font-extrabold uppercase tracking-[0.2em]"
              maxLength={8}
            />
            {novaPlaca.length > 0 && !placaValida && (
              <p className="text-xs font-semibold text-rose-600">Formato inválido</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">
              Motivo da troca
            </Label>
            <Select value={motivo} onValueChange={setMotivo}>
              <SelectTrigger className="h-12 rounded-xl">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {MOTIVOS_TROCA.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="desc-motivo" className="text-xs font-semibold uppercase tracking-wider">
              Descrição{' '}
              <span className="text-muted-foreground text-[10px] normal-case tracking-normal">
                (opcional)
              </span>
            </Label>
            <Textarea
              id="desc-motivo"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Detalhes da troca..."
              rows={2}
              maxLength={300}
              className="rounded-xl"
            />
          </div>

          <div className="border-border/40 bg-card/40 rounded-2xl border p-3 text-xs">
            <p className="text-foreground flex items-center gap-1.5 font-semibold">
              <ShieldAlert className="text-primary h-3.5 w-3.5" />
              Politica anti-fraude
            </p>
            <p className="text-muted-foreground mt-1 leading-relaxed">
              Apos a troca, voce nao podera mudar a placa de novo por algumas semanas. Se ja fez
              lavagem recente, a troca pode estar bloqueada — aguarde a carencia.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={carregando}
            className="h-12 rounded-xl"
          >
            Cancelar
          </Button>
          <Button
            variant="accent"
            onClick={() => onSubmit(placaNorm, motivo, descricao)}
            disabled={!podeEnviar || carregando}
            className="h-12 flex-1 rounded-xl"
          >
            {carregando ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Trocando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" /> Confirmar troca
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Modal: Vendi este carro
// Exportado pra ser reusado na tela de detalhe do veiculo.
// =============================================================================
export function VenderVeiculoDialog({
  veiculo,
  onSubmit,
  onClose,
  carregando,
}: {
  veiculo: Veiculo;
  onSubmit: (motivo: string) => void;
  onClose: () => void;
  carregando: boolean;
}) {
  const [confirmacao, setConfirmacao] = useState('');
  const [motivo, setMotivo] = useState('');
  const podeEnviar = confirmacao.toUpperCase() === veiculo.num_placa;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-border/40 rounded-3xl sm:max-w-md">
        <DialogHeader className="space-y-2.5">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 to-rose-700 text-white shadow-lg shadow-rose-500/30">
            <Tag className="h-5 w-5" />
          </div>
          <DialogTitle className="text-display-sm text-center font-extrabold tracking-tight text-rose-600 dark:text-rose-400">
            Vendi este carro
          </DialogTitle>
          <DialogDescription className="text-center">
            Vamos remover o veículo{' '}
            <span className="text-foreground font-bold tracking-wider">{veiculo.num_placa}</span> do
            seu cadastro e liberar a vaga.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
            <p className="flex items-center gap-1.5 font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" />
              Importante
            </p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs">
              <li>O veículo não podera mais ser lavado pela sua assinatura</li>
              <li>A placa fica bloqueada alguns dias antes de ser cadastrada de novo</li>
              <li>O histórico de lavagens permanece arquivado pra LGPD</li>
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="motivo-venda"
              className="text-xs font-semibold uppercase tracking-wider"
            >
              Observação{' '}
              <span className="text-muted-foreground text-[10px] normal-case tracking-normal">
                (opcional)
              </span>
            </Label>
            <Textarea
              id="motivo-venda"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Vendi pra um amigo, troquei por outro carro..."
              rows={2}
              maxLength={300}
              className="rounded-xl"
            />
          </div>

          <div className="space-y-1.5">
            <Label
              htmlFor="confirma-placa"
              className="text-xs font-semibold uppercase tracking-wider"
            >
              Pra confirmar, digite{' '}
              <span className="text-foreground font-bold tracking-wider">{veiculo.num_placa}</span>
            </Label>
            <Input
              id="confirma-placa"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value.toUpperCase())}
              className="h-12 rounded-xl text-lg font-extrabold uppercase tracking-[0.2em]"
              placeholder={veiculo.num_placa}
            />
          </div>
        </div>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={carregando}
            className="h-12 rounded-xl"
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => onSubmit(motivo)}
            disabled={!podeEnviar || carregando}
            className="h-12 flex-1 rounded-xl"
          >
            {carregando ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Removendo...
              </>
            ) : (
              <>Confirmar venda</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
